/**
 * Sentry (L10) — frontend error monitoring.
 *
 * Captures uncaught errors, unhandled rejections, ErrorBoundary
 * fallbacks, and on-error session replays. See LGPD posture below.
 *
 * Loading strategy (PageSpeed PR1):
 *   - The Sentry SDK + replay integration used to live in the main
 *     bundle, paid by every visitor on the critical render path. We
 *     now `await import("@sentry/browser")` so Vite splits it into
 *     its own chunk that's fetched off the critical path.
 *   - Switched from @sentry/react to @sentry/browser (PageSpeed
 *     follow-up): we never used the React-specific helpers
 *     (Sentry.ErrorBoundary, Sentry.withProfiler) — our ErrorBoundary
 *     calls captureReactError() with `Sentry.withScope` directly, and
 *     react-router integration was never wired up. @sentry/browser
 *     is meaningfully smaller without changing the API surface we use.
 *   - main.tsx schedules initSentry() via requestIdleCallback (with a
 *     setTimeout fallback for Safari < 16.4) so the first paint isn't
 *     blocked by Sentry's bootstrap work.
 *   - Tradeoff: errors thrown DURING module evaluation of main.tsx
 *     itself land outside Sentry's reporting window. This is rare in
 *     practice and acceptable; React render errors caught by
 *     ErrorBoundary still report normally.
 *
 * LGPD posture:
 *   - maskAllText: true — replay never captures typed text
 *   - blockAllMedia: true — replay never captures images/video
 *   - sendDefaultPii: false — no IP/cookie headers shipped by default
 *
 * The DSN is public (it's in the client bundle anyway). Source maps
 * upload via @sentry/vite-plugin in CI when SENTRY_AUTH_TOKEN +
 * SENTRY_ORG + SENTRY_PROJECT env vars are set.
 */

// Type-only imports are erased at build time, so naming @sentry/browser
// here does NOT pull the SDK back into this chunk — the runtime import
// below stays the only one that costs bytes.
import type { ErrorEvent as SentryErrorEvent } from "@sentry/browser";
import { isStaleChunkMessage } from "@/lib/lazy-with-reload";

const DSN =
  "https://2e7f3ade88cf0c762ff7a22df1e03c69@o4511267353526272.ingest.us.sentry.io/4511267416506368";

/**
 * Errors dropped unconditionally, before beforeSend ever runs.
 *
 * Only put a pattern here when the error is noise in EVERY case it can
 * occur. Anything whose severity depends on context belongs in
 * beforeSend, where the event's tags are readable — see
 * dropStaleChunkNoise for why that distinction cost us a silent bug.
 *
 *  - Browser-extension content scripts (password managers, Safari/
 *    Chrome extensions) messaging their background context — e.g.
 *    Sentry ORBRASIL-FRONTEND-9 "Invalid call to runtime.sendMessage().
 *    Tab not found." Our app never calls the WebExtension API, so any
 *    of these strings is an injected extension, not us.
 *  - Transient WebGL context loss on iOS Safari: the GPU reclaims the
 *    context, gl.createShader() returns null, and three.js throws
 *    "shaderSource … WebGLShader" from inside R3F's rAF render loop.
 *    Model3DViewer now pauses + restores on webglcontextlost/restored
 *    so the viewer self-heals; this unavoidable in-flight throw (the
 *    render that loses the context, before the listener can pause) is
 *    non-actionable. Matches Safari ("must be an instance of
 *    WebGLShader") + Chrome ("is not of type 'WebGLShader'").
 *    Sentry ORBRASIL-FRONTEND-A.
 */
export const IGNORE_ERRORS: readonly RegExp[] = [
  /runtime\.sendMessage/i,
  /Tab not found/i,
  /Extension context invalidated/i,
  /WebGLShader/i,
];

/**
 * Tag set by captureReactError. Its presence means the error reached
 * the app-level ErrorBoundary, i.e. the reader lost the page.
 */
export const BOUNDARY_TAG = "caught_by";
export const BOUNDARY_TAG_VALUE = "error_boundary";

/**
 * Stale-chunk filter, applied per-event rather than as a blanket
 * `ignoreErrors` entry.
 *
 * A stale chunk that `lazyWithReload` / `withStaleChunkRecovery`
 * recovered from is genuinely non-actionable: the reader saw a reload,
 * not a broken page, and there is nothing to fix. Those still get
 * dropped, and during a deploy window there can be a lot of them.
 *
 * But an import failure that reached the ErrorBoundary is the exact
 * opposite — the reader lost the whole page, header and footer
 * included, because that import had no recovery wrapper. Blanket-
 * filtering on the message meant those crashes never reported: the
 * only monitoring for the worst version of this bug was the user
 * telling us. That's how the 14 unguarded imports fixed in #286 went
 * unnoticed across several deploys.
 *
 * So: same message, opposite meaning, and the tag is what separates
 * them. Anything the boundary caught reports regardless of message.
 *
 * This has to live in beforeSend rather than as a smarter
 * `ignoreErrors` entry: `ignoreErrors` is enforced by the
 * InboundFilters event processor, which runs BEFORE beforeSend and
 * discards the event outright — a tag can't rescue it from there.
 */
export function dropStaleChunkNoise(
  event: SentryErrorEvent,
): SentryErrorEvent | null {
  if (event.tags?.[BOUNDARY_TAG] === BOUNDARY_TAG_VALUE) return event;

  const messages = [
    event.message ?? "",
    ...(event.exception?.values ?? []).map((v) => v?.value ?? ""),
  ];
  return messages.some(isStaleChunkMessage) ? null : event;
}

// Resolved once initSentry() finishes; consumers awaiting reportError
// or captureException reuse the same SDK module — dynamic imports
// are cached by the bundler.
let sentryReady: Promise<typeof import("@sentry/browser")> | null = null;

export async function initSentry(): Promise<void> {
  if (typeof window === "undefined") return; // SSR/prerender: skip
  if (sentryReady) return; // already initialised

  // Dynamic import so the SDK + replay integration becomes its own
  // chunk, fetched off the critical render path.
  sentryReady = import("@sentry/browser");
  const Sentry = await sentryReady;

  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.MODE,
    release: `orbrasil-frontend@${__BUILD_HASH__}`,

    // Performance traces — 10% sample in prod, all in dev.
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Replays — expensive; keep off by default, enable only on errors.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // LGPD: don't ship IP or cookie headers.
    sendDefaultPii: false,

    // Copied because Sentry's option type is mutable and IGNORE_ERRORS
    // is readonly — the SDK never mutates it, but the types disagree.
    ignoreErrors: [...IGNORE_ERRORS],

    // Drop noise: localhost dev runs, plus recovered stale chunks.
    beforeSend(event) {
      if (event.request?.url?.includes("localhost")) return null;
      return dropStaleChunkNoise(event);
    },
  });
}

/**
 * Report a caught error from outside the bootstrap path (e.g.
 * ErrorBoundary). Awaits Sentry being ready and forwards via
 * `withScope` so callers can attach context. No-op if Sentry hasn't
 * been initialised yet AND init isn't pending — in that pre-init
 * window the error is still console-logged elsewhere.
 */
export async function captureReactError(
  error: Error,
  context: Record<string, unknown> = {},
): Promise<void> {
  if (typeof window === "undefined") return;
  // If init hasn't started yet (e.g. an error fires before
  // requestIdleCallback got around to scheduling it), kick it off so
  // we still capture the report.
  if (!sentryReady) initSentry();
  const Sentry = await sentryReady!;
  Sentry.withScope((scope) => {
    for (const [k, v] of Object.entries(context)) scope.setContext(k, v as Record<string, unknown>);
    // Marks the event as "the reader lost the page", which is what
    // exempts it from the stale-chunk filter in beforeSend.
    scope.setTag(BOUNDARY_TAG, BOUNDARY_TAG_VALUE);
    Sentry.captureException(error);
  });
}
